import React from "react";
import { Form, Col } from "antd";
import { useTranslation } from "react-i18next";
import { MailOutlined } from "@ant-design/icons";

// graphql

// common
import { ModalLayout } from "common/modal";
import { CheckboxGroup, CustomCheckbox } from "./CustomCheckbox";

// styling
import { LinkedText, FormCard, CardTitle } from "./SignUpStyle";
import {
  AlreadyAMember,
  BigYellowButton,
  CustomInput,
  Linkify,
  WrapMeUp,
} from "./SignUpFormStyle";

const { ModalRow, ModalCol } = ModalLayout;

type RegistrationFormProps = {
  loading: boolean;
};
const RegistrationForm = ({ loading }: RegistrationFormProps) => {
  const { t, i18n } = useTranslation("new_sign_up");

  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <>
      <CardTitle>
        <div className="icon">
          <MailOutlined />
        </div>
        {t("Sign Up With Email").toUpperCase()}
      </CardTitle>
      <FormCard>
        <Form.Item
          name="locale"
          hidden
          noStyle
          initialValue={i18n.language}
          style={{ display: "none" }}
        >
          <CustomInput type="hidden" name="locale" />
        </Form.Item>
        <Form.Item
          name="timeZone"
          hidden
          noStyle
          initialValue={detectedTimezone}
          style={{ display: "none" }}
        >
          <CustomInput type="hidden" name="timeZone" />
        </Form.Item>
        <ModalRow>
          <ModalCol xs={24}>
            <Form.Item
              name="givenName"
              rules={[
                { required: true, message: t("First name is required.") },
              ]}
            >
              <CustomInput
                type="text"
                placeholder={`*${t("First name")}`}
                name="givenName"
                size="large"
              />
            </Form.Item>
          </ModalCol>
        </ModalRow>
        <ModalRow>
          <ModalCol xs={24}>
            <Form.Item
              name="familyName"
              rules={[
                { required: true, message: t("Family name is required.") },
              ]}
            >
              <CustomInput
                type="text"
                placeholder={`*${t("Family name")}`}
                name="familyName"
                size="large"
              />
            </Form.Item>
          </ModalCol>
        </ModalRow>

        <ModalRow>
          <ModalCol xs={24}>
            <Form.Item
              name="email"
              rules={[{ required: true, message: t("Email is required.") }]}
            >
              <CustomInput
                type="email"
                placeholder={`*${t("Email")}`}
                name="email"
                size="large"
              />
            </Form.Item>
          </ModalCol>
        </ModalRow>

        <ModalRow gutter={20}>
          <ModalCol xs={24} sm={24}>
            <Form.Item
              name="password"
              rules={[{ required: true, message: t("Password is required.") }]}
            >
              <CustomInput
                type="password"
                placeholder={`*${t("Password")}`}
                name="password"
                size="large"
              />
            </Form.Item>
          </ModalCol>
        </ModalRow>
        <CheckboxGroup>
          <Form.Item name="sendTips" valuePropName="checked" initialValue>
            <CustomCheckbox name="sendTips">
              {t("Send me offers and study tips.")}
            </CustomCheckbox>
          </Form.Item>
          <Form.Item
            name="agreedPrivacyPolicy"
            rules={[
              {
                // For checkbox, have to do this kind of validation.
                // Cannot simply use required:true like for input, will lead to inconsistency.
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          t("Agreement to LTL's Privacy Policy is required.")
                        )
                      ),
              },
            ]}
            valuePropName="checked"
          >
            <CustomCheckbox name="agreedPrivacyPolicy">
              {t("*I agree to the ")}
              <LinkedText
                className="linked-text"
                tabIndex={0}
                href={t("PRIVACY_POLICY_URL")}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("Privacy Policy")}
              </LinkedText>
            </CustomCheckbox>
          </Form.Item>
        </CheckboxGroup>
        <WrapMeUp>
          <AlreadyAMember>
            {t("Already a Member?")}{" "}
            <Linkify href="/login">{t("Log in here")}</Linkify>
          </AlreadyAMember>
        </WrapMeUp>
      </FormCard>
      <ModalRow>
        <Col xs={1} sm={2} md={0} />
        <Col xs={22} sm={20} md={24}>
          <Form.Item style={{ width: "100%", marginTop: "10px" }}>
            <BigYellowButton loading={loading} type="primary" htmlType="submit">
              {t("Sign up").toUpperCase()}
            </BigYellowButton>
          </Form.Item>
        </Col>
        <Col xs={1} sm={2} md={0} />
      </ModalRow>
    </>
  );
};

export default RegistrationForm;
